import React, { useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import palette from "src/theme/palette";
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from "@consts";
import { HyroButton, HyroTabs } from "@hyro-components";
import HyroBreadcrumbs from "@hyro-components/hyro-breadcrumbs/HyroBreadcrumbs";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
} from "@hyro-components/hyro-button/HyroButton.consts";
import AccountEditorFields from "../AccountEditorFields";
import ConfirmationDialog from "../ConfirmationDialog";
import useAccountEditorController from "../useAccountEditorController";
import { DIALOG_TYPES } from "../AccountEditorConsts";
import { TABS, PAGE_TITLE } from "./AccountSettings.consts";
import SubscriptionTab from "./subscription/SubscriptionTab";
import Dialog from "@hyro-components/Dialogs/HyroUiDialog";

const BREADCRUMBS = ["Account management", "Account settings"];
const BASE_URL = "/admin/account-management";

const AccountSettingsContent = ({
  accountData,
  isAccountContext,
  isSaving,
  isConfirmationOpen,
  onSave,
  onSubmit,
  onConfirmationClose,
  onDiscard,
  error,
  errorText,
  setError,
  searchParams,
}) => {
  const {
    newAccountData,
    isLoadingSftpSettings,
    isSaveDisabled,
    isReadOnly,
    invalidIpIndexes,
    invalidSupportedDomainIndexes,
    invalidExtraSupportedEmailIndexes,
    handleTextFieldChange,
  } = useAccountEditorController({
    accountData,
    type: DIALOG_TYPES.edit,
    error,
    errorText,
    onConfirm: () => { },
    onResetError: () => setError(null),
    isAccountContext,
  });

  const timezoneOptions = TIMEZONE_OPTIONS.map((option) =>
    option.key === DEFAULT_TIMEZONE
      ? { ...option, chipText: "System default" }
      : option
  );

  const { showSalesforceData } = useFlags();
  const [, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || TABS.GENERAL;
  const isGeneralTab = activeTab === TABS.GENERAL;

  useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: TABS.GENERAL }, { replace: true });
    }
  }, []);

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };
  const accountId = newAccountData?.accountId?.value;

  return (
    <>
      {!isAccountContext && (
        <HyroBreadcrumbs breadcrumbs={BREADCRUMBS} baseUrl={BASE_URL} />
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        sx={{ height: 36 }}
      >
        <Typography variant="h5" data-testid="account-settings-title">
          {PAGE_TITLE}
        </Typography>
        {isGeneralTab && (
          <HyroButton
            data-testid="account-settings-save-changes"
            loading={isSaving}
            onClick={onSave}
            disabled={isSaveDisabled}
            variant={BUTTON_VARIANTS.CONTAINED}
            color={BUTTON_COLORS.PRIMARY}
          >
            Save changes
          </HyroButton>
        )}
      </Stack>

      <HyroTabs
        selectedValue={activeTab}
        onTabChange={handleTabChange}
        tabsSpec={[
          {
            label: "General",
            value: TABS.GENERAL,
            content: (
              <>
                <Typography mb={2} variant="h6" data-testid="general-settings-title">
                  General settings
                </Typography>
                <Box
                  sx={{
                    bgcolor: palette.system[0],
                    border: 1,
                    borderColor: palette.grey[200],
                    borderRadius: 1,
                    p: 3,
                  }}
                >
                  <Box sx={{ maxWidth: 552 }}>
                    <AccountEditorFields
                      type={DIALOG_TYPES.edit}
                      data={newAccountData}
                      timezoneOptions={timezoneOptions}
                      onChange={handleTextFieldChange}
                      isLoadingSftpSettings={isLoadingSftpSettings}
                      invalidIpIndexes={invalidIpIndexes}
                      invalidSupportedDomainIndexes={invalidSupportedDomainIndexes}
                      invalidExtraSupportedEmailIndexes={invalidExtraSupportedEmailIndexes}
                      isReadOnly={isReadOnly}
                    />
                  </Box>
                </Box>
              </>
            ),
          },
          ...(showSalesforceData
            ? [
              {
                label: "Subscription",
                value: TABS.SUBSCRIPTION,
                content: (
                  <SubscriptionTab
                    accountId={accountId}
                    isAccountContext={isAccountContext}
                    salesforceAccountId={
                      newAccountData?.salesforceAccountId?.value
                    }
                  />
                ),
              },
            ]
            : []),
        ]}
      />

      <ConfirmationDialog
        open={isConfirmationOpen}
        isLoading={isSaving}
        onDiscard={onDiscard}
        onClose={onConfirmationClose}
        onSubmit={() => onSubmit(newAccountData)}
        isSaveDisabled={isSaveDisabled}
        accountName={newAccountData?.name?.value}
      />

      {error && (
        <Dialog
          open={!!error}
          variant="acknowledgement"
          title="Something went wrong"
          submitButtonText="Got it"
          handleSubmit={() => setError(null)}
        >
          <Typography variant="body1" color="text.800">
            {errorText}
          </Typography>
        </Dialog>
      )}
    </>
  );
};

export default AccountSettingsContent;
